import { FunnelIcon, UserGroupIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import phoneBg from "../../assets/images/phone-bg.png";
import Button from "../../components/Button";
import { Badge } from "../../components/Calls/Badge";
import { CallStatusIcon } from "../../components/Calls/CallStatusIcon";
import { ConversationFilters } from "../../components/Calls/ConversationFilters";
import { PhoneBurner } from "../../components/Calls/PhoneBurner";
import { PhoneBurnerIcon } from "../../components/Calls/PhoneBurnerIcon";
import { SearchBar } from "../../components/SearchBar";
import { CallStatus, CallStatusLabel } from "../../enums/CallStatus";
import { ConversationType, getClientLeadTypes } from "../../enums/ConversationType";
import { CallDirection } from "../../enums/TwilioCalls";
import { Client } from "../../models/Client";
import { ConversationFilter } from "../../models/ConversationFilter";
import { Lead } from "../../models/Lead";
import { CallProps, TwilioCall } from "../../models/TwilioCall";
import { messageDateFormat } from "../../utils/functions";
import { ConferenceModal } from "../../components/Calls/ConferenceModal";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { fetchCalls } from "../../state/twilio/actions";
import { number } from "prop-types";
import { nextPage } from "../../state/commissions/action";
import { useDispatch } from "react-redux";
import { LoadingMask } from "../../components/LoadingMask";
import { AppDispatch } from "../../state/store";
import { Call, Device } from "@twilio/voice-sdk";
import { TwilioState } from "../../state/twilio/slice";
import { Direction } from "react-toastify/dist/utils";

interface Props {
  makePhoneCall: (phone: string) => void;
}
export const Phone = (props: Props) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const twilio:TwilioState = useSelector((state: any) => state.twilio);
  const [selectedCall, setSelectedCall] = useState<TwilioCall>();
  const [filter, setFilter] = useState("");
  const [filters, setFilters] = useState<ConversationFilter>({
    selectedTypes: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isPhoneBurnerOpen, setIsPhoneBurnerOpen] = useState(false);
  const [isConferenceOpen, setConferenceOpen] = useState(false);
  const [isConferenceModalOpen, setIsConferenceModalOpen] = useState(false);
  const { t } = useTranslation();
  const currentUser = useSelector((state: any) => state.users.currentUser);
  const twilioUserPhone = currentUser?.main_twilio_number?.twilio_phone;
  const scrollRef = useRef<HTMLDivElement>(null);
  const outboundPageToken = twilio.outboundPageToken;
  const inboundPageToken = twilio.inboundPageToken;
  const isLoading = twilio.isLoading;
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const handleScroll = () => {
      const div = scrollRef.current;
      if (!div) return;
      const atBottom =
        div.scrollTop + div.clientHeight >= div.scrollHeight - 50;

      if (outboundPageToken !== null && inboundPageToken !== null && atBottom && !isLoading && (outboundPageToken || inboundPageToken)) {
        // console.log("Tokens:", {outboundPageToken,inboundPageToken,isLoading,}); // log para development / ver los tokens porvenir
        dispatch(
          fetchCalls({ pageSize: 10, outboundPageToken, inboundPageToken })
        );
      }
    };
    const div = scrollRef.current;
    if (div) {
      div.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (div) {
        div.removeEventListener("scroll", handleScroll);
      }
    };
  }, [dispatch, outboundPageToken, inboundPageToken, isLoading]);

  useEffect(() => {
    let flag =
      (twilio?.phoneBurner?.remainingList?.length ?? 0) > 0 ||
      Boolean(twilio?.phoneBurner?.currentCall);

    setIsPhoneBurnerOpen(flag);
  }, [twilio?.phoneBurner?.remainingList?.length,twilio?.phoneBurner?.currentCall,]);

  
  function formatPhoneNumber(number: string): string { // solamente numeros de USA
    const cleaned = number.replace(/\D/g, '');
    const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);
    if (!match) return number;
      return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  const callsWithType = useMemo(() => {
    const calls = twilio.calls ?? [];

    return calls.map((call: CallProps) => {
      
      const callDirection = call.callDirection;
      const callType = call.callType;
      const callName = call.callName;
      let callerOrCallee = undefined;
      let refactoredDirection = callDirection;
      const callStatus = call.callStatus;
      const callStartTime = call.callStartTime;
      
      if (callDirection === "inbound"){
        callerOrCallee = call.callFrom;
        refactoredDirection = CallDirection.INBOUND;
      } else {
        callerOrCallee = call.callTo;
        refactoredDirection = CallDirection.OUTBOUND_DIAL;
      }

      if (callType === "Client" ){
        const formattedCallPhone = callerOrCallee ? formatPhoneNumber(callerOrCallee) : "";
        return{
          ...call,
          conversationType: ConversationType.CLIENTS,
          name: callName,
          direction: refactoredDirection,
          status: callStatus,
          startTime: callStartTime,
          shownNumber: formattedCallPhone
        }
      } else if (callType === "Lead"){
        const formattedCallPhone = callerOrCallee ? formatPhoneNumber(callerOrCallee) : "";
        return {
          ...call,
          conversationType: ConversationType.LEADS,
          name: callName,
          direction: refactoredDirection,
          status: callStatus,
          startTime: callStartTime,
          shownNumber: formattedCallPhone
        }
      } else { 
        const formattedCallPhone = callerOrCallee ? formatPhoneNumber(callerOrCallee) : "";
        // console.log(formattedCallPhone)
        return {
            ...call,
            conversationType: ConversationType.OTHER,
            name: formattedCallPhone,
            direction: refactoredDirection,
            status: callStatus,
            startTime: callStartTime,
            shownNumber: undefined
          }
      }
    });

  }, [twilio, twilioUserPhone]);


  const handleSearchChange = (event: any) => {
    setFilter(event.target.value);
  };

  const filteredCalls = useMemo(() => {
  let list = callsWithType ?? [];

  // Filtrar por tipos (Clients / Leads / Other) manteniendo el orden original
  if (filters?.selectedTypes?.length) {
    const allowed = new Set(filters.selectedTypes);
    list = list.filter((c: any) => allowed.has(String(c.conversationType)));
  }

  // Filtrar por texto sobre el name o número 
  const q = (filter ?? '').trim().toLowerCase();
  if (!q) return list;

  return list.filter((c: any) => {
    const name = String(c.name ?? '').toLowerCase();
    const number = String(c.shownNumber ?? c.callFrom ?? c.callTo ?? '').toLowerCase();
    return name.includes(q) || number.includes(q);
  });
}, [callsWithType, filters?.selectedTypes, filter]);

  const handleCallClick = (call: any) => {
    setSelectedCall(call)
    const callDirection = call.callDirection;
    let callerOrCallee = undefined;
    if (callDirection === "inbound"){
      callerOrCallee = call.callFrom;
    } else {
      callerOrCallee = call.callTo;
    }
    if (
      twilio.device &&
      (twilio.twilioDeviceState === Device.State.Registered)
    ) {
      props.makePhoneCall(callerOrCallee);
    }
  };

  const showOrHideFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const updateFilterValues = (fil: ConversationFilter) => {
    setFilters(fil);
  };

  const openPhoneBurner = () => {
    setIsPhoneBurnerOpen((prev) => !prev);
  };

  return (
    <div className="w-full grow p-10 flex flex-col">
      <div className="text-black max-h-full  w-screen md:w-full relative overflow-x-auto bg-white rounded-lg shadow transition-all duration-500 ease-in-out overflow-hidden">
        {twilio.twilioDeviceState === Device.State.Registered  ? (
          <div
            className="w-full grow flex flex-row overflow-hidden"
            style={{ height: "calc(100vh - 154px)" }}
          >
            <div
              className={classNames({
                "flex flex-col overflow-x-auto p-5 gap-4 lg:w-1/3 lg:border-r md:overflow-auto block":
                  true,
              })}
            >
              <div className="w-full flex justify-between flex-wrap">
                <h2 className="text-md font-semibold">Calls</h2>
                <div className="flex gap-1">
                  <div className="relative flex justify-center lg:items-center gap-2 cursor-pointer">
                    <Button
                      isDisabled={!twilio.areTwilioCredentialsValid}
                      iconOnly
                      isSecondary
                      onClick={showOrHideFilters}
                    >
                      <FunnelIcon className="w-6 text-gold" />
                    </Button>
                    {showFilters && (
                      <ConversationFilters
                        filters={filters}
                        updateFilterValues={updateFilterValues}
                      />
                    )}
                    {!twilio.conferenceId && (
                      <Button
                        isDisabled={!twilio.areTwilioCredentialsValid}
                        iconOnly
                        isSecondary
                        onClick={() => {
                          setIsConferenceModalOpen(true);
                        }}
                      >
                        <UserGroupIcon className="w-6 text-gold" />
                      </Button>
                    )}
                    {/* {isLoading && <LoadingMask/>} */}
                  </div>

                  <Button
                    isDisabled={!twilio.areTwilioCredentialsValid}
                    iconOnly
                    isSecondary
                    onClick={openPhoneBurner}
                  >
                    <PhoneBurnerIcon />
                  </Button>
                </div>
              </div>

              <SearchBar onChange={handleSearchChange} value={filter} />
              <div ref={scrollRef} className="overflow-y-auto">
                {filteredCalls?.map((call: CallProps) => {
                  return (
                    <div
                      key={call.callSid}
                      className={classNames({
                        "flex flex-row  gap-3 p-3 hover:bg-neutral-200 cursor-pointer shadow-sm hover:bg-gray":
                          true,
                      })}
                    >
                      <div className="flex flex-col grow gap-2"
                        onClick={() => handleCallClick(call)}
                        >
                        <div className="flex flex-row justify-between">
                          <div className="flex gap-2">
                            <div className={"font-semibold"}>{(call).callName}</div>
                            <Badge type={call.conversationType ?? ConversationType.OTHER} />
                          </div>
                          <div className="text-sm font-light text-neutral-500">
                            {messageDateFormat(call.callStartTime || "")}
                          </div>
                        </div>
                        <div className={"text-xs font-light text-neutral-500"}>{call.shownNumber}</div>
                        {/* TODO <div className={"text-xs font-light text-neutral-500"}>{call.shownNumber}</div>*/}
                        <div className="flex gap-2">
                          <CallStatusIcon
                            type={call.callDirection as CallDirection}
                            status={call.callStatus as CallStatus}
                          />
                          <span className="text-sm text-neutral-500">
                            {CallStatusLabel(call.callStatus || "")}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="w-full flex justify-center mt-4">
                  {isLoading && <ArrowPathIcon className="w-8 h-8 text-gold animate-spin bg-gray rounded-full p-1" />}
                </div>
              </div>
            </div>

            <div className="flex flex-col relative w-2/3">
              {isPhoneBurnerOpen ? (
                <PhoneBurner endCall={() => {
                  twilio.currentCall?.reject();
                  twilio.device?.disconnectAll();
                }} />
              ) : (
                <>
                  {selectedCall?.conversationType !== ConversationType.LEADS &&
                    selectedCall?.conversationType !==
                      ConversationType.CLIENTS && (
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <img
                          src={phoneBg}
                          alt="Asureis Logo"
                          className="h-32"
                        />
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div
            className="text-l text-center relative inset-y-2/4"
            style={{ height: "calc(100vh - 164px)" }}
          >
            {t("TWILIO_CONNECTION_ERROR")}
          </div>
        )}
      </div>
      <ConferenceModal
        isOpen={isConferenceModalOpen}
        onClose={() => {
          setIsConferenceModalOpen(false);
        }}
        callSid={selectedCall?.sid || ""}
        isNewConference={true}
      />
    </div>
  );
};
